import {
  Button,
  CloseButton,
  Dialog,
  Flex,
  Portal,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { LightMode } from "../ui/color-mode";
import { AppColors } from "@/styles/colors";
import { useEffect, useState } from "react";
import { toaster } from "../ui/toaster";
import { SlotSampleData } from "@/pages/grain-requests/slot-requests/schema/slot-requests.schema";
import { getAvailableSlots } from "@/services/slot-requests.service";
import { StandardTable } from "../table/StandardTable";
import { NoDataSmall } from "../common/section-components/NoDataSmall";
import { PaginationComponent } from "../pagination/PaginationComponent";

interface Props {
  setOpen: (flag: boolean) => void;
  open: boolean;
  loadingAccept: boolean;
  handleAssign: (selectedSlots: SlotSampleData[]) => Promise<void>;
  accountNumber: number;
  requestId: number
}

export const AssignSlotsModal = ({
  open,
  setOpen,
  loadingAccept,
  handleAssign,
  accountNumber,
  requestId,
}: Props) => {
  const [isActive, setIsActive] = useState<boolean>(false);

  const [page, setPage] = useState<number>(1)

  const handlePageChange = (page: number) => {
    setPage(page);
   
    fetchSlots({
      "P.Pagina": page,
      "P.Records": 10
    })
  }

  useEffect(() => {
    setIsActive(true);
  }, []);
  const [loadingSlots, setLoadingSlots] = useState<boolean>(false);

  const [slots, setSlots] = useState<{
    cupos: SlotSampleData[] | undefined,
    totalRecords: number
    totalPages: number
  }>();
  const [selection, setSelection] = useState<string[]>([]);

  const fetchSlots = async (filters: any) => {
    try {
      setLoadingSlots(true);

      const response = await getAvailableSlots({
        nroCuenta: Number(accountNumber),
        idSolicitud: requestId,
        "P.Pagina": filters["P.Pagina"] ?? 1,
        "P.Records": filters["P.Records"] ?? 10
      });

      setSlots(response);
      setSelection([]);
    } catch (error) {
      toaster.create({
        type: "error",
        description: "Ocurrió un error al obtener los cupos",
      });
      throw error;
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (isActive && open) {
      fetchSlots({});
    }
  }, [isActive, open]);

  const items =
    slots?.cupos?.map((slot) => ({
      ...slot,
      rowKey: String(slot?.id),
    })) ?? [];

  const hasSelection = selection?.length > 0;
  const indeterminate = hasSelection && selection.length < items.length;

  const selectedSlots =
    slots?.cupos?.filter((slot) => selection.includes(String(slot?.id))) ??
    [];

  const headerElements = [
    {
      label: "Fecha descarga",
      value: "fechaDescarga",
    },
    {
      label: "Cantidad de cupos",
      value: "cantidadCuposTotales"
    },
    {
      label: "A planta",
      value: "cantidadCuposAPlanta",
    },
    {
      label: "Directos",
      value: "cantidadCuposDirectos",
    },
  ];

  const submitAssign = async () => {
    await handleAssign(selectedSlots);
  };
  return (
    <Dialog.Root
      placement="center"
      lazyMount
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <LightMode>
            <Dialog.Content
              data-testid="assign-slots-modal"
              width="90vw"
              maxWidth="1100px"
            >
              <Dialog.Header paddingY="16px" paddingLeft="24px">
                <Dialog.Title
                  fontWeight={600}
                  color={AppColors.GRAY_TEXT}
                  fontSize="18px"
                >
                  Asignar solicitud de cupos (AGROBIT)
                </Dialog.Title>
              </Dialog.Header>
              <Dialog.Body
                paddingX="24px"
                paddingY="8px"
                width="100%"
                overflow="hidden"
              >
                <Flex flexDir="column" gap="12px">
                  <Text fontSize="16px" color={AppColors.GRAY_TEXT}>
                    Seleccioná los cupos disponibles para asignar.
                  </Text>

                  {loadingSlots ? (
                    <Flex justifyContent="center" paddingY="18px">
                      <Spinner />
                    </Flex>
                  ) : null}

                  {Boolean(!loadingSlots && !items?.length) && <NoDataSmall />}

                  {Boolean(!loadingSlots && items?.length) && (
                    <StandardTable
                      indeterminate={indeterminate}
                      headers={headerElements}
                      items={items}
                      selection={selection}
                      setSelection={setSelection}
                      noActions
                      rowId="id"
                      fullWidth
                      maxHeight="45vh"
                    />
                  )}
                  <PaginationComponent 
                      page={page}
                      handlePageChange={handlePageChange}
                      pageSize={10}
                      count={slots?.totalRecords || 0}
                    />
               
                </Flex>
              </Dialog.Body>
              <Dialog.Footer paddingY="16px" paddingX="24px">
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline" paddingX="16px" fontWeight={600}>
                    Cancelar
                  </Button>
                </Dialog.ActionTrigger>
                <Button
                  paddingX="16px"
                  bgColor={AppColors.GREEN_PRIMARY}
                  fontWeight={600}
                  onClick={submitAssign}
                  loading={loadingAccept}
                  disabled={loadingAccept || Boolean(!selectedSlots?.length)}
                >
                  Aceptar
                </Button>
              </Dialog.Footer>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </LightMode>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
